import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { queryClient } from "App";
import {
  getMessages,
  addNewMessage,
  updateMessage,
  deleteMessage,
} from "app/services/schoolReportingServices/complaint.service";
import MessagesContainer from "./MessagesContainer";

// Main Container Component
const ComplaintConversationPreview = ({
  complaintId,
  complaintNumber,
  currentUser = null,
}) => {
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [newMessage, setNewMessage] = useState("");
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);

  // Fetch complaint messages
  const {
    data: messagesResponse,
    isLoading,
    error,
  } = useQuery(
    ["complaintMessages", complaintId],
    () => getMessages(complaintId),
    {
      enabled: !!complaintId,
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to fetch complaint messages"
        );
      },
    }
  );

  // Add message mutation
  const { mutate: addMessage, isLoading: isAddingMessage } = useMutation(
    addNewMessage,
    {
      onSuccess: () => {
        toast.success("Message added successfully");
        setNewMessage("");
        queryClient.invalidateQueries(["complaintMessages", complaintId]);
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to add message");
      },
    }
  );

  // Update message mutation
  const { mutate: editMessage, isLoading: isEditingMessage } = useMutation(
    ({ messageId, content }) => updateMessage(messageId, { message: content }),
    {
      onMutate: ({ messageId }) => {
        setEditingMessageId(messageId);
      },
      onSuccess: () => {
        toast.success("Message updated successfully");
        queryClient.invalidateQueries(["complaintMessages", complaintId]);
        setEditingMessageId(null);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to update message"
        );
        setEditingMessageId(null);
      },
    }
  );

  // Delete message mutation
  const { mutate: removeMessage, isLoading: isDeletingMessage } = useMutation(
    deleteMessage,
    {
      onMutate: (messageId) => {
        setDeletingMessageId(messageId);
      },
      onSuccess: () => {
        toast.success("Message deleted successfully");
        queryClient.invalidateQueries(["complaintMessages", complaintId]);
        setDeletingMessageId(null);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to delete message"
        );
        setDeletingMessageId(null);
      },
    }
  );

  const messages = messagesResponse?.data?.list || [];

  const handleExpand = (index) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedMessages(newExpanded);
  };

  const handleEditMessage = (messageId, newContent) => {
    editMessage({ messageId, content: newContent });
  };

  const handleDeleteMessage = (messageId) => {
    removeMessage(messageId);
  };

  const handleSendMessage = (messageData) => {
    // Handle both old format (string) and new format (object with attachment)
    const messageText =
      typeof messageData === "string" ? messageData : messageData?.message;
    const attachment =
      typeof messageData === "object" ? messageData?.attachment : null;

    if (!messageText?.trim() && !attachment) {
      toast.error("Please enter a message or attach a file");
      return;
    }

    const payload = {
      complaintDocId: complaintId,
      message: messageText?.trim() || "",
    };

    // Add attachment to payload if exists
    if (attachment) {
      payload.attachment = attachment; // In real implementation, this would be the uploaded file URL
    }

    addMessage(payload);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading conversation...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Failed to load conversation</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <MessagesContainer
        messages={messages}
        expandedMessages={expandedMessages}
        onExpand={handleExpand}
        newMessage={newMessage}
        setNewMessage={setNewMessage}
        onSendMessage={handleSendMessage}
        isAddingMessage={isAddingMessage}
        currentUser={currentUser}
        onEditMessage={handleEditMessage}
        onDeleteMessage={handleDeleteMessage}
        editingMessageId={editingMessageId}
        deletingMessageId={deletingMessageId}
      />
    </Box>
  );
};

export default ComplaintConversationPreview;
